import { Component, inject, input, OnInit, signal, computed, output } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { PointIdDto, PointLinkDto, PointLinkedMailDto, PointLinkTypeDto } from '@shared/interfaces/dtos/points';
import { BusinessRouter } from '@shared/models/dev';
import { Point } from '@shared/models/point';
import { PointsService } from '@shared/services/points.service';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { distinctUntilChanged, finalize, skip, switchMap } from 'rxjs';
import { NotificationSuccess } from 'src/app/core/models';
import { Notifier } from 'src/app/core/services';
import { ApplicationConfiguration } from 'src/app/core/services/application-configuration';

@Component({
  selector: 'idel-point-links',
  templateUrl: './point-links.component.html',
  styleUrl: './point-links.component.scss',
})
export class PointLinksComponent implements OnInit {
  private readonly service = inject(PointsService);
  private readonly appConfig = inject(ApplicationConfiguration);
  private readonly businessRouter = inject(BusinessRouter);
  private readonly notifier = inject(Notifier);

  public readonly point = input.required<Point>();

  public readonly linkDeleted = output<void>();

  public readonly hasAccessToICourrier = this.appConfig.tenant().hasICourrier();
  public readonly pointIdsSuggestions = signal<PointIdDto[]>([]);
  public readonly mailIdsSuggestions = signal<{ id: number; objetSynthese?: string | null }[]>([]);
  public readonly pointLinkTypesOptions = signal<PointLinkTypeDto[]>([]);
  public readonly fetchingPointLinkTypes = signal<boolean>(false);
  public readonly fetchingPointLinks = signal<boolean>(false);
  public readonly fetchingPointLinkedMails = signal<boolean>(false);
  public readonly deletingPointLink = signal<boolean>(false);
  public readonly deletingPointLinkedMail = signal<boolean>(false);

  public readonly selectedPointId = signal<PointIdDto | null>(null);
  public readonly selectedPointLinkType = signal<PointLinkTypeDto | null>(null);
  public readonly selectedMailId = signal<PointIdDto | null>(null);

  public readonly creatingPointLink = signal<boolean>(false);
  public readonly creatingPointLinkWithMail = signal<boolean>(false);

  public readonly canCreatePointLink = computed<boolean>(() => {
    const selectedPointId = this.selectedPointId();
    const selectedPointLinkType = this.selectedPointLinkType();

    if (!selectedPointId || !selectedPointLinkType) return false;

    return true;
  });
  public readonly canCreatePointLinkWithMail = computed<boolean>(() => {
    const selectedMailId = this.selectedMailId();

    return !!selectedMailId;
  });

  public readonly pointLinks = signal<PointLinkDto[]>([]);
  public readonly pointLinkedMails = signal<PointLinkedMailDto[]>([]);

  constructor() {
    toObservable(this.point)
      .pipe(
        distinctUntilChanged((previous, current) => previous.id === current.id),
        skip(1),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        this.init();
      });
  }

  ngOnInit(): void {
    this.init();
  }

  private init(): void {
    this.emptyEverything();
    this.fetchPointLinkTypes();
    this.fetchBothPointLinksLists();
  }

  private emptyEverything(): void {
    this.selectedMailId.set(null);
    this.selectedPointId.set(null);
    this.selectedPointLinkType.set(null);
    this.pointLinks.set([]);
    this.pointLinkedMails.set([]);
  }

  private fetchBothPointLinksLists(): void {
    this.fetchPointLinks();
    this.fetchPointLinkedMails();
  }

  private fetchPointLinks(): void {
    this.fetchingPointLinks.set(true);

    this.service
      .fetchPointLinks(this.point().id)
      .pipe(finalize(() => this.fetchingPointLinks.set(false)))
      .subscribe({
        next: (links) => this.pointLinks.set(links),
      });
  }

  private fetchPointLinkedMails(): void {
    this.fetchingPointLinkedMails.set(true);

    this.service
      .fetchOnePoint(this.point().id)
      .pipe(
        switchMap((point) => this.service.fetchPointLinkedMails(point.courriersInitiateursListe as unknown as number[]))
      )
      .pipe(finalize(() => this.fetchingPointLinkedMails.set(false)))
      .subscribe({
        next: (mails) => this.pointLinkedMails.set(mails ?? []),
      });
  }

  private fetchPointLinkTypes(): void {
    this.fetchingPointLinkTypes.set(true);

    this.service
      .fetchPointLinkTypes()
      .pipe(finalize(() => this.fetchingPointLinkTypes.set(false)))
      .subscribe({
        next: (types) => this.pointLinkTypesOptions.set(types),
      });
  }

  public onSearchPointIds(event: AutoCompleteCompleteEvent): void {
    const { query: id } = event;

    if (+id < 0 || isNaN(+id) || id.trim() === '') {
      this.selectedPointId.set(null);
    }

    this.service.fetchPointsSuggestions(+id.trim()).subscribe({
      next: (suggestions) => this.pointIdsSuggestions.set(suggestions),
    });
  }

  public onSearchMailIds(event: AutoCompleteCompleteEvent): void {
    const { query: id } = event;

    if (+id < 0 || isNaN(+id) || id.trim() === '') {
      this.selectedMailId.set(null);
    }

    this.service.fetchMailsSuggestions(+id.trim()).subscribe({
      next: (suggestions) => this.mailIdsSuggestions.set(suggestions),
    });
  }

  public createPointLink(): void {
    if (this.creatingPointLink() || !this.canCreatePointLink()) return;

    const selectedPointId = this.selectedPointId();
    const selectedPointLinkType = this.selectedPointLinkType();

    if (!this.canCreatePointLink() || !selectedPointId || !selectedPointLinkType) return;

    this.creatingPointLink.set(true);
    this.service
      .createLinkBetweenPoints({
        currentPointId: this.point().id,
        linkedPointId: selectedPointId.id,
        linkType: selectedPointLinkType.description,
      })
      .pipe(finalize(() => this.creatingPointLink.set(false)))
      .subscribe({
        next: () => {
          this.selectedPointId.set(null);
          this.selectedPointLinkType.set(null);

          this.notifier.showSuccess(new NotificationSuccess({ message: 'Lien créé entre les points' }));

          this.fetchPointLinks();
        },
      });
  }

  public createPointLinkWithMail(): void {
    if (this.creatingPointLinkWithMail() || !this.canCreatePointLinkWithMail()) return;

    this.creatingPointLinkWithMail.set(true);
    const selectedMailId = this.selectedMailId();

    if (!this.canCreatePointLinkWithMail() || !selectedMailId) return;

    this.service
      .createLinkBetweenPointAndMail(this.point().id, { mailIds: [selectedMailId.id] })
      .pipe(finalize(() => this.creatingPointLinkWithMail.set(false)))
      .subscribe({
        next: () => {
          this.notifier.showSuccess(
            new NotificationSuccess({ message: 'Lien créé entre le courrier et le point actuel' })
          );
          this.selectedMailId.set(null);
          this.fetchPointLinkedMails();
        },
      });
  }

  public onDeleteLink(point: PointLinkDto): void {
    if (this.deletingPointLink()) return;

    this.deletingPointLink.set(true);

    this.service
      .deleteLinkBetweenPoints({
        currentPointId: this.point().id,
        linkedPointId: point.linkPoint.id,
        linkType: point.typeLiaison,
      })
      .pipe(finalize(() => this.deletingPointLink.set(false)))
      .subscribe({
        next: () => {
          this.notifier.showSuccess(new NotificationSuccess({ message: 'Lien supprimé entre les points' }));
          this.fetchPointLinks();
          this.linkDeleted.emit();
        },
      });
  }

  public onDeleteLinkedMail(mail: PointLinkedMailDto): void {
    if (this.deletingPointLinkedMail()) return;

    this.deletingPointLinkedMail.set(true);

    this.service
      .deleteLinkBetweenPointAndMail(this.point().id, { mailIds: [mail.id] })
      .pipe(finalize(() => this.deletingPointLinkedMail.set(false)))
      .subscribe({
        next: () => {
          this.notifier.showSuccess(new NotificationSuccess({ message: 'Lien supprimé avec le courrier' }));
          this.fetchPointLinkedMails();
          this.linkDeleted.emit();
        },
      });
  }

  public openPointPage(pointLink: PointLinkDto): void {
    this.businessRouter.goToAPoint(pointLink.linkPointId, { newTab: true });
  }

  public openMailPage(mailLink: PointLinkedMailDto): void {
    this.businessRouter.goToAMail(mailLink.id);
  }
}
